import React, { useContext } from "react";
import styled, { keyframes } from "styled-components";
import { Container } from "react-bootstrap";

import GlobalContext from "../../context/GlobalContext";

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
    box-shadow: 0 0 0 0 var(--nd-accent-subtle);
  }
  50% {
    opacity: 0.55;
    box-shadow: 0 0 0 10px transparent;
  }
`;

const blink = keyframes`
  0%, 49% { opacity: 1; }
  50%, 100% { opacity: 0; }
`;

const SectionStyled = styled.section`
  padding: 128px 0;
  background: var(--nd-black);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(circle, var(--nd-border) 0.5px, transparent 0.5px);
    background-size: 12px 12px;
    opacity: 0.3;
    pointer-events: none;
  }
`;

const Inner = styled.div`
  position: relative;
  text-align: center;
  max-width: 720px;
  margin: 0 auto;
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-secondary);
  margin-bottom: 32px;
`;

const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--nd-accent);
  animation: ${pulse} 2s ease-in-out infinite;
`;

const CTATitle = styled.h2`
  font-family: 'Doto', 'Space Mono', monospace;
  font-size: 56px;
  font-weight: 700;
  color: var(--nd-text-display);
  letter-spacing: -0.02em;
  line-height: 1.05;
  margin: 0 0 24px;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const Caret = styled.span`
  display: inline-block;
  margin-left: 0.1ch;
  color: var(--nd-accent);
  animation: ${blink} 1.1s steps(1) infinite;
`;

const CTADesc = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 13px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-secondary);
  margin: 0 0 48px;
`;

const Dot = styled.span`
  display: inline-block;
  margin: 0 10px;
  color: var(--nd-text-disabled);
`;

const PrimaryButton = styled.button`
  position: relative;
  font-family: 'Space Mono', monospace;
  font-size: 13px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 14px 32px;
  min-height: 48px;
  border-radius: 999px;
  border: none;
  background: var(--nd-text-display);
  color: var(--nd-black);
  cursor: pointer;
  transition: transform 250ms cubic-bezier(0.25, 0.1, 0.25, 1), box-shadow 250ms cubic-bezier(0.25, 0.1, 0.25, 1);
  outline: none;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 24px rgba(255, 255, 255, 0.08);
  }

  &:active {
    transform: translateY(0);
  }

  &::after {
    content: '→';
    margin-left: 12px;
    display: inline-block;
    transition: transform 250ms cubic-bezier(0.25, 0.1, 0.25, 1);
  }

  &:hover::after {
    transform: translateX(4px);
  }
`;

const SecondaryButton = styled.a`
  font-family: 'Space Mono', monospace;
  font-size: 13px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 14px 32px;
  min-height: 48px;
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  border: 1px solid var(--nd-border-visible);
  background: transparent;
  color: var(--nd-text-primary) !important;
  cursor: pointer;
  text-decoration: none !important;
  transition: border-color 250ms cubic-bezier(0.25, 0.1, 0.25, 1);
  outline: none;
  margin-left: 16px;
  &:hover { border-color: var(--nd-text-primary); }
  @media (max-width: 480px) {
    margin-left: 0;
    margin-top: 12px;
  }
`;

const Hint = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin: 24px 0 0;
`;

const CTA = () => {
  const gContext = useContext(GlobalContext);

  return (
    <SectionStyled>
      <Container>
        <Inner>
          <StatusBadge>
            <StatusDot />
            Available — Q2 2026
          </StatusBadge>
          <CTATitle>
            Want to see if we're a fit?<Caret>_</Caret>
          </CTATitle>
          <CTADesc>
            30-min call <Dot>·</Dot> No pitch deck <Dot>·</Dot> No pressure
          </CTADesc>
          <PrimaryButton
            aria-label="Book a call"
            onClick={(e) => {
              e.preventDefault();
              gContext.toggleContact();
            }}
          >
            Book a call
          </PrimaryButton>
          <SecondaryButton
            href="/Dioscuro_Services_Brochure.pdf"
            download
            aria-label="Download brochure"
          >
            Download brochure
          </SecondaryButton>
          <Hint>Avg reply &lt; 24h</Hint>
        </Inner>
      </Container>
    </SectionStyled>
  );
};

export default CTA;
